import React from 'react';
import { cn } from '@/lib/utils';
import {
  LayoutDashboard,
  FileText,
  Settings,
  ChevronLeft
} from 'lucide-react';
import { useIsAndroid } from '@/hooks/use-android';
import { useUserPrivileges } from '@/context/UserPrivilegesContext';
import { TAB_VISIBILITY_PRIVILEGES } from '@/lib/privilegeConstants';

interface SidebarProps {
  currentPage: string;
  onNavigate: (page: string) => void;
  isCollapsed: boolean;
  onToggle: () => void;
  username?: string;
}

const Sidebar = ({ currentPage, onNavigate, isCollapsed, onToggle, username }: SidebarProps) => {
  const isAndroid = useIsAndroid();
  const { hasAnyPrivilege, loading: privilegesLoading, privilegeIds } = useUserPrivileges();

  const allMenuItems = [
    {
      title: 'Transactions',
      icon: <LayoutDashboard className="w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6 lg:w-4 lg:h-4 xl:w-5 xl:h-5 2xl:w-6 2xl:h-6 flex-shrink-0" />,
      value: 'transactions'
    },
    {
      title: 'Administrator',
      icon: <Settings className="w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6 lg:w-4 lg:h-4 xl:w-5 xl:h-5 2xl:w-6 2xl:h-6 flex-shrink-0" />,
      value: 'administrator'
    },
    {
      title: 'Reports',
      icon: <FileText className="w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6 lg:w-4 lg:h-4 xl:w-5 xl:h-5 2xl:w-6 2xl:h-6 flex-shrink-0" />,
      value: 'reports'
    },
  ];

  // Show tab if user has at least one privilege for that section. While loading, show all. If no privileges at all (API empty/fail), show all as fallback to avoid blank screen.
  const menuItems = privilegesLoading
    ? allMenuItems
    : privilegeIds.size === 0
      ? allMenuItems
      : allMenuItems.filter((item) => {
          const required = TAB_VISIBILITY_PRIVILEGES[item.value];
          if (!required || required.length === 0) return true;
          return hasAnyPrivilege(required);
        });

  console.log('[Sidebar] Privileges loading:', privilegesLoading, 'privilegeIds.size:', privilegeIds.size, 'visible tabs:', menuItems.map((m) => m.value));

  return (
    <div className={cn(
      "h-full bg-white text-gray-600 p-3 sm:p-4 md:p-4 lg:p-3 xl:p-4 2xl:p-4 flex flex-col transition-all duration-300 border-r border-gray-200",
      // Narrower collapsed width on desktop to reduce empty space on large screens
      isCollapsed ? "w-14 sm:w-16 md:w-16 lg:w-16 xl:w-16 2xl:w-16" : "w-56 sm:w-64 md:w-72 lg:w-56 xl:w-64 2xl:w-72",
      "overflow-y-auto"
    )}>
      {/* Logo and Title Section - Hidden on Android */}
      {!isAndroid && (
        <div className="relative mb-8">
          <div className={cn(
            "flex items-center gap-4 transition-all duration-300",
            isCollapsed ? "lg:justify-center" : "justify-start"
          )}>
            <div className="w-10 h-10 sm:w-12 sm:h-12 md:w-12 md:h-12 lg:w-10 lg:h-10 xl:w-12 xl:h-12 2xl:w-12 2xl:h-12 rounded-full bg-gray-100 p-1.5 sm:p-2 md:p-2 lg:p-1.5 xl:p-2 2xl:p-2 shadow-lg hover:shadow-gray-200 hover:scale-110 transition-all duration-300 border border-gray-200">
              <div className="w-full h-full rounded-full overflow-hidden">
                <img 
                  src="/lovable-uploads/2b818f9e-899e-485a-aa82-050dac779b78.png" 
                  alt="CATCHUS.IN Logo" 
                  className="w-full h-full object-cover rounded-full"
                />
              </div>
            </div>
            {(!isCollapsed || window.innerWidth < 1024) && (
              <span className="text-sm sm:text-base md:text-lg lg:text-sm xl:text-base 2xl:text-lg font-bold tracking-wider text-gray-900 whitespace-nowrap">
                CATCHUS.IN
              </span>
            )}
          </div>
          <button
            onClick={onToggle}
            className={cn(
              "absolute -right-4 top-6 bg-gray-200 rounded-full p-1.5 hover:bg-gray-300 transition-all duration-300 hidden lg:block",
              isCollapsed ? "-right-4" : "-right-4"
            )}
          >
            <ChevronLeft className={cn(
              "w-4 h-4 transition-transform duration-300 text-gray-600",
              isCollapsed ? "rotate-180" : ""
            )} />
          </button>
        </div>
      )}

            {/* Welcome Message for Android */}
      {isAndroid && username && (
        <div className={cn(
          "relative mb-6 bg-gray-50 rounded-lg border border-gray-200 transition-all duration-300",
          isCollapsed ? "p-2" : "p-4"
        )}>
          <div className={cn(
            "text-left transition-all duration-300",
            isCollapsed ? "hidden" : "block"
          )}>
            <span className="text-gray-500 text-sm block">Welcome back,</span>
            <span className="text-gray-900 font-semibold text-base truncate block">{username}</span>
          </div>
          {/* Back icon for mobile - positioned in marked area */}
          <button
            onClick={() => {
              // Close sidebar on mobile
              if (window.innerWidth < 1024) {
                // Dispatch event to close sidebar
                window.dispatchEvent(new CustomEvent('closeSidebar'));
              }
            }}
            className="lg:hidden absolute top-1/2 right-4 transform -translate-y-1/2 bg-gray-200 rounded-lg p-1.5 hover:bg-gray-300 transition-all duration-300"
          >
            <ChevronLeft className="w-4 h-4 text-gray-600" />
          </button>
          {/* Collapse button for Android */}
          <button
            onClick={onToggle}
            className={cn(
              "absolute -right-4 top-1/2 transform -translate-y-1/2 bg-gray-200 rounded-full p-1.5 hover:bg-gray-300 transition-all duration-300 hidden lg:block",
              isCollapsed ? "-right-4" : "-right-4"
            )}
          >
            <ChevronLeft className={cn(
              "w-4 h-4 transition-transform duration-300 text-gray-600",
              isCollapsed ? "rotate-180" : ""
            )} />
          </button>
        </div>
      )}

      {/* Navigation Menu */}
      <nav className="flex-1">
        <ul className="space-y-2">
          {menuItems.map((item) => (
            <li key={item.value}>
              <button
                onClick={() => onNavigate(item.value)}
                className={cn(
                  "w-full flex items-center gap-2 sm:gap-3 md:gap-3 lg:gap-2 xl:gap-3 2xl:gap-3 px-2 sm:px-3 md:px-3 lg:px-2 xl:px-3 2xl:px-3 py-1.5 sm:py-2 md:py-2 lg:py-1.5 xl:py-2 2xl:py-2 rounded-lg transition-colors",
                  "hover:bg-gray-100",
                  currentPage === item.value ? "bg-gray-100 text-blue-600" : "text-gray-600",
                  isCollapsed ? "lg:justify-center" : "justify-start"
                )}
              >
                {item.icon}
                {(!isCollapsed || window.innerWidth < 1024) && (
                  <span className="text-xs sm:text-sm md:text-base lg:text-xs xl:text-sm 2xl:text-base whitespace-nowrap">{item.title}</span>
                )}
              </button>
            </li>
          ))}
        </ul>
      </nav>
    </div>
  );
};

export default Sidebar; 